import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of } from 'rxjs';
import {
  switchMap,
  withLatestFrom,
  filter,
  distinctUntilChanged,
  catchError
} from 'rxjs/operators';

import { EventService } from '@shared/services/event.service';
import { distinctUntilObjectChanged } from '@shared/utilities/rxjs.util';
import { MonitoringService } from '@core/services/monitoring.service';

import { OpportunitySummaryService } from './opportunity-summary.service';
import { OpportunitySummary } from '../models/opportunity';

export type OpportunityEvent =
  | 'UPDATED'
  | 'DELETED'
  | 'NOTHING'
  | 'UPDATE_MODE'
  | 'SUBMITTED';

export type AliBrokerAccreditationStatus = 
  | 'UNKNOWN'
  | 'ACCREDITED'
  | 'NOT_ACCREDITED';

@Injectable({
  providedIn: 'root'
})
export class OpportunityEventService extends EventService<OpportunityEvent> {
  private _opportunityId: string | null = null;
  private refresh$: BehaviorSubject<null> = new BehaviorSubject(null);
  private selectedOpportunityIdSubject = new BehaviorSubject<string | null>(
    null
  );
  private selectedOpportunitySummarySubject =
    new ReplaySubject<OpportunitySummary | null>(1);
  private aliBrokerAccreditationSubject = new BehaviorSubject<AliBrokerAccreditationStatus>('UNKNOWN');
  private loadErrorSubject = new BehaviorSubject<Error | null>(null);
  
  // Subject for single opportunity updates (used by planner after workflow transitions)
  private singleOpportunityUpdatedSubject = new Subject<string>();
  
  /**
   * Observable for single opportunity updates.
   * Emits the opportunityId when a single opportunity needs to be refreshed
   * (e.g., after a workflow transition in the planner).
   */
  public singleOpportunityUpdated$ = this.singleOpportunityUpdatedSubject.asObservable();

  public selectedOpportunityId$: Observable<string> =
    this.selectedOpportunityIdSubject.asObservable().pipe(
      filter((id): id is string => id !== null),
      distinctUntilChanged()
    );

  public selectedOpportunityIdOrNull$: Observable<string | null> =
    this.selectedOpportunityIdSubject
      .asObservable()
      .pipe(distinctUntilObjectChanged());

  public selectedOpportunitySummary$ = this.selectedOpportunitySummarySubject
    .asObservable()
    .pipe(
      filter((x): x is OpportunitySummary => x !== null),
      distinctUntilObjectChanged()
    );

  public selectedOpportunitySummaryOrNull$ =
    this.selectedOpportunitySummarySubject
      .asObservable()
      .pipe(distinctUntilObjectChanged());

  public aliBrokerAccreditation$: Observable<AliBrokerAccreditationStatus> =
    this.aliBrokerAccreditationSubject.asObservable().pipe(distinctUntilChanged());

  public loadError$ = this.loadErrorSubject.asObservable().pipe(
    filter((error): error is Error => error !== null)
  );

  constructor(
    private opportunityService: OpportunitySummaryService,
    private monitoringService: MonitoringService
  ) {
    super('NOTHING');
    this.resetSubjects();
    this.on('UPDATED').subscribe(() => this.refreshData());
    this.on('SUBMITTED').subscribe(() => this.refreshData());
    this.getLatestData();
  }

  private getLatestData() {
    this.refresh$
      .pipe(
        withLatestFrom(this.selectedOpportunityIdOrNull$),
        switchMap(([_, opportunityId]) =>
          opportunityId
            ? this.opportunityService.getSummaryForDisplay(
                opportunityId,
                true,
                true
              ).pipe(
                catchError((error) => {
                  // Log error to Application Insights for monitoring
                  this.monitoringService.logException(
                    error instanceof Error ? error : new Error('Failed to load opportunity summary'),
                    undefined,
                    { opportunityId }
                  );
                  // Emit error so guard can react immediately
                  this.loadErrorSubject.next(error);
                  // Return null to keep subscription alive
                  return of(null);
                })
              )
            : of(null)
        )
      )
      .subscribe((summary) => {
        if (summary !== null) {
          this.selectedOpportunitySummarySubject.next(summary);
          // Clear error state on successful load
          this.loadErrorSubject.next(null);
        }
      });
  }

  public updateOpportunityId(opportunityId: string | null) {
    if (!opportunityId) {
      this.resetSubjects();
    }
    if (this._opportunityId !== opportunityId) {
      this._opportunityId = opportunityId;
      this.selectedOpportunityIdSubject.next(opportunityId);
      // Clear any previous error state when navigating to a new opportunity
      this.loadErrorSubject.next(null);
      this.refreshData();
    }
  }

  public refreshData() {
    this.refresh$.next(null);
  }

  /**
   * Fire event to refresh a single opportunity in the planner.
   * Used after workflow transitions to avoid full board refresh.
   * @param opportunityId The ID of the opportunity to refresh
   */
  public fireSingleOpportunityUpdated(opportunityId: string): void {
    this.singleOpportunityUpdatedSubject.next(opportunityId);
  }

  public updateAliBrokerAccreditation(isAccredited: boolean) {
    this.aliBrokerAccreditationSubject.next(isAccredited ? 'ACCREDITED' : 'NOT_ACCREDITED');
  }

  public checkAliBrokerAccreditation(opportunityId: string): Observable<boolean> {
    return this.opportunityService.checkBrokerAccreditation(opportunityId);
  }

  private resetSubjects() {
    this.selectedOpportunitySummarySubject.next(null);
    this.aliBrokerAccreditationSubject.next('UNKNOWN');
    this.loadErrorSubject.next(null);
  }
}
